'use client';

import Icon from '@/components/ui/AppIcon';

const features = [
  {
    category: 'Delivery & Speed',
    items: [
      { name: 'Delivery Time', express: '2-3 days', freight: '5-7 days', warehousing: 'N/A', customs: '1-2 days' },
      { name: 'Priority Handling', express: true, freight: false, warehousing: false, customs: true },
      { name: 'Same-Day Pickup', express: true, freight: false, warehousing: false, customs: false },
    ],
  },
  {
    category: 'Tracking & Support',
    items: [
      { name: 'Real-Time Tracking', express: true, freight: true, warehousing: true, customs: true },
      { name: 'Dedicated Account Manager', express: false, freight: true, warehousing: true, customs: false },
      { name: '24/7 Support', express: true, freight: true, warehousing: true, customs: true },
    ],
  },
  {
    category: 'Insurance & Protection',
    items: [
      { name: 'Insurance Coverage', express: 'Up to $500', freight: 'Up to $5,000', warehousing: 'Custom', customs: 'Up to $1,000' },
      { name: 'Signature Confirmation', express: true, freight: true, warehousing: false, customs: true },
      { name: 'Damage Protection', express: true, freight: true, warehousing: true, customs: false },
    ],
  },
  {
    category: 'Additional Services',
    items: [
      { name: 'Customs Documentation', express: false, freight: true, warehousing: false, customs: true },
      { name: 'Inventory Management', express: false, freight: false, warehousing: true, customs: false },
      { name: 'White Glove Service', express: false, freight: true, warehousing: false, customs: false },
    ],
  },
];

const services = [
  { id: 'express', name: 'Express', color: 'primary' },
  { id: 'freight', name: 'Freight', color: 'secondary' },
  { id: 'warehousing', name: 'Warehousing', color: 'accent' },
  { id: 'customs', name: 'Customs', color: 'primary' },
];

export default function FeatureComparison() {
  const renderCell = (value: any) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Icon name="CheckIcon" size={20} className="text-success mx-auto" />
      ) : (
        <Icon name="XMarkIcon" size={20} className="text-muted-foreground mx-auto" />
      );
    }
    return <span className="text-sm text-foreground">{value}</span>;
  };

  return (
    <section className="py-16 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
            Feature Comparison
          </h2>
          <p className="text-lg text-muted-foreground">
            Compare features across all service tiers to find the best fit
          </p>
        </div>

        {/* Comparison Table - Desktop */}
        <div className="hidden lg:block bg-card rounded-xl shadow-card border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50 border-b border-border">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Features</th>
                  {services.map((service) => (
                    <th key={service.id} className="px-6 py-4 text-center text-sm font-semibold text-foreground">
                      {service.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {features.map((category, catIndex) => (
                  <>
                    <tr key={`cat-${catIndex}`} className="bg-muted/30">
                      <td colSpan={5} className="px-6 py-3 text-sm font-semibold text-foreground">
                        {category.category}
                      </td>
                    </tr>
                    {category.items.map((item, itemIndex) => (
                      <tr
                        key={`item-${catIndex}-${itemIndex}`}
                        className="border-b border-border hover:bg-muted/20 transition-colors"
                      >
                        <td className="px-6 py-4 text-sm text-muted-foreground">{item.name}</td>
                        <td className="px-6 py-4 text-center">{renderCell(item.express)}</td>
                        <td className="px-6 py-4 text-center">{renderCell(item.freight)}</td>
                        <td className="px-6 py-4 text-center">{renderCell(item.warehousing)}</td>
                        <td className="px-6 py-4 text-center">{renderCell(item.customs)}</td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Comparison Cards - Mobile */}
        <div className="lg:hidden space-y-6">
          {services.map((service) => (
            <div key={service.id} className="bg-card rounded-xl shadow-card border border-border p-6">
              <h3 className="text-xl font-heading font-semibold text-foreground mb-4">{service.name}</h3>
              <div className="space-y-4">
                {features.map((category, catIndex) => (
                  <div key={catIndex}>
                    <h4 className="text-sm font-semibold text-foreground mb-2">{category.category}</h4>
                    <ul className="space-y-2">
                      {category.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{item.name}</span>
                          <span className="flex items-center">
                            {renderCell(item[service.id as keyof typeof item])}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}